/// <amd-module name="Reporting/Visuals/LayoutComponents/Widgets/LayoutComponents" />
import * as React from "react";
import DataQualityService from "Reporting/DataQuality/DataQualityService";
import { VisualHeader } from "Reporting/Visuals/VisualHeader";
import ChartComponentRenderer from "Widgets/ChartComponent";
import { IDemandHandler } from "Widgets/Demands/IDemandHandler";
import { LayoutState } from "Widgets/LayoutState";
import ScalarComponentRenderer from "Widgets/ScalarComponent";
import SubmetricsComponentRenderer from "Widgets/SubmetricsComponent";
import { DemandType } from "./Demands/DemandType";

/** This contract describes behaviors supported by all Layout Factories, for preparing layouts for use by the LayoutEngine. */
export interface ILayoutComponentFactory<T> {
    /**
     * Ensures all demands required by this component are registered.
     */
    registerDemands(demandHandler: IDemandHandler): void;

    /** Render layout component with current state.*/
    render(state: T): React.ReactElement<any>;
}

export class ScalarLayoutFactory implements ILayoutComponentFactory<LayoutState> {
    public registerDemands(demandHandler: IDemandHandler): void {
        HeaderLayoutFactory.registerDemands(demandHandler);
        ScalarComponentRenderer.registerDemands(demandHandler);
    }

    public render(state: LayoutState): React.ReactElement<any> {
        return (
            <div className="scalar-layout">
                <div className="header-row">{HeaderLayoutFactory.render(state)}</div>
                {ScalarComponentRenderer.renderScalarVerticalComponent(state.scalarData)}
            </div>
        );
    }
}

export class ScalarAndChromelessChartLayoutFactory implements ILayoutComponentFactory<LayoutState> {
    public registerDemands(demandHandler: IDemandHandler): void {
        HeaderLayoutFactory.registerDemands(demandHandler);
        ScalarComponentRenderer.registerDemands(demandHandler);
        ChartComponentRenderer.registerDemands(demandHandler);
    }

    public render(state: LayoutState): React.ReactElement<any> {
        return (
            <div className="scalar-and-chart-layout">
                <div className="header-row">
                    {HeaderLayoutFactory.render(state)}
                    {ScalarComponentRenderer.renderScalarHorizontalComponent(state.scalarData)}
                </div>
                {ChartComponentRenderer.renderChromeless(state.chartData)}
            </div>
        );
    }
}

export class MinimalChartLayoutFactory implements ILayoutComponentFactory<LayoutState> {
    public registerDemands(demandHandler: IDemandHandler): void {
        ChartComponentRenderer.registerDemands(demandHandler);
    }

    public render(state: LayoutState): React.ReactElement<any> {
        return <div className="minimal-chart-layout">{ChartComponentRenderer.renderMinimalChromeless(state.chartData)}</div>;
    }
}

export class ScalarAndChartLayoutFactory implements ILayoutComponentFactory<LayoutState> {
    public registerDemands(demandHandler: IDemandHandler): void {
        HeaderLayoutFactory.registerDemands(demandHandler);
        ScalarComponentRenderer.registerDemands(demandHandler);
        ChartComponentRenderer.registerDemands(demandHandler);
    }

    public render(state: LayoutState): React.ReactElement<any> {
        return (
            <div className="scalar-and-chart-layout">
                <div className="header-row">
                    {HeaderLayoutFactory.render(state)}
                    {ScalarComponentRenderer.renderScalarHorizontalComponent(state.scalarData)}
                </div>
                {ChartComponentRenderer.render(state.chartData!)}
            </div>
        );
    }
}

export class ScalarAndSubmetricsAndChartLayoutFactory implements ILayoutComponentFactory<LayoutState> {
    public registerDemands(demandHandler: IDemandHandler): void {
        HeaderLayoutFactory.registerDemands(demandHandler);
        ScalarComponentRenderer.registerDemands(demandHandler);
        SubmetricsComponentRenderer.registerDemands(demandHandler);
        ChartComponentRenderer.registerDemands(demandHandler);
    }

    public render(state: LayoutState): React.ReactElement<any> {
        return (
            <div className="scalar-and-submetrics-and-chart-layout">
                <div className="header-row">
                    {HeaderLayoutFactory.render(state)}
                    {ScalarComponentRenderer.renderScalarHorizontalComponent(state.scalarData)}
                </div>
                {SubmetricsComponentRenderer.renderSubmetricsComponent(state.submetricsData)}
                {ChartComponentRenderer.render(state.chartData)}
            </div>
        );
    }
}

// This is just a helper class to render the title and subtitle on a widget.
class HeaderLayoutFactory {
    public static registerDemands(demandHandler: IDemandHandler): void {
        demandHandler.registerDemand(DemandType.title);
        demandHandler.registerDemand(DemandType.subtitle);
    }

    public static render(state: LayoutState): React.ReactElement<any> {
        if (DataQualityService.evaluate(state.warningsData)) {
            return (
                <VisualHeader
                    title={state.title.text}
                    hideTitle={state.title.hideTitle}
                    subtitle={state.subtitle != null ? state.subtitle.text : undefined}
                    dataQualityWarning={DataQualityService.evaluate(state.warningsData)}
                />
            );
        } else {
            return (
                <VisualHeader
                    title={state.title.text}
                    hideTitle={state.title.hideTitle}
                    subtitle={state.subtitle != null ? state.subtitle.text : undefined}
                />
            );
        }
    }
}
